import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  ogLocale?: string;
  /** Open Graph type (es. `article` per i post blog). */
  ogType?: string;
  /** Open Graph Article: date ISO (solo con ogType article). */
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  /** es. "index, follow" per pagine indicizzabili */
  robots?: string;
  alternates?: Array<{ hrefLang: string; href: string }>;
  structuredData?: object;
  /** Versione markdown alternativa (rel alternate type=text/markdown). */
  markdownAlternateHref?: string;
}

/**
 * Componente SEO per gestire meta tag dinamici e structured data
 * Usa solo per pagine pubbliche (non PrivateTemplate)
 */
const SEO_JSONLD_ATTR = 'data-seo-jsonld';

export const SEO: React.FC<SEOProps> = ({
  title = 'Kipify - Analytics Platform per E-commerce',
  description = "Connetti Shopify, Google Ads, Facebook Ads e Google Analytics in un'unica dashboard. Monitora ROAS, ACOS, margini e revenue con KPI personalizzati.",
  canonical,
  ogImage = 'https://app.kipify.io/images/hero-header-kipify.png',
  ogLocale = 'it_IT',
  ogType,
  articlePublishedTime,
  articleModifiedTime,
  robots,
  alternates = [],
  structuredData,
  markdownAlternateHref,
}) => {
  const location = useLocation();
  const baseUrl = 'https://app.kipify.io';
  const fullCanonical = canonical || `${baseUrl}${location.pathname}`;

  useEffect(() => {
    // Aggiorna title
    document.title = title;

    // robots / googlebot (SPA: serve cleanup per evitare "leak" tra route)
    const ensureMeta = (name: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      return el;
    };

    if (robots) {
      ensureMeta('robots').setAttribute('content', robots);
      ensureMeta('googlebot').setAttribute('content', robots);
    } else {
      document.querySelector('meta[name="robots"]')?.remove();
      document.querySelector('meta[name="googlebot"]')?.remove();
    }

    // Aggiorna o crea meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', description);

    // Aggiorna o crea canonical
    let linkCanonical = document.querySelector('link[rel="canonical"]');
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.setAttribute('rel', 'canonical');
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.setAttribute('href', fullCanonical);

    // Aggiorna Open Graph
    const updateOGTag = (property: string, content: string) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    document
      .querySelectorAll('meta[data-dynamic-seo="article-og"]')
      .forEach((node) => node.remove());
    document.querySelector('meta[property="og:type"]')?.remove();

    updateOGTag('og:title', title);
    updateOGTag('og:description', description);
    updateOGTag('og:url', fullCanonical);
    updateOGTag('og:image', ogImage);
    updateOGTag('og:locale', ogLocale);
    updateOGTag('og:site_name', 'Kipify');

    const setArticleOg = (property: string, content: string) => {
      const tag = document.createElement('meta');
      tag.setAttribute('property', property);
      tag.setAttribute('content', content);
      tag.setAttribute('data-dynamic-seo', 'article-og');
      document.head.appendChild(tag);
    };

    if (ogType) {
      updateOGTag('og:type', ogType);
    }
    if (ogType === 'article') {
      if (articlePublishedTime) {
        setArticleOg('article:published_time', articlePublishedTime);
      }
      if (articleModifiedTime) {
        setArticleOg('article:modified_time', articleModifiedTime);
      }
    }

    const ensureTwitter = (name: string, content: string) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', name);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
      tag.setAttribute('data-dynamic-seo', 'article-og');
    };
    if (ogType === 'article') {
      ensureTwitter('twitter:card', 'summary_large_image');
      ensureTwitter('twitter:title', title);
      ensureTwitter('twitter:description', description);
      ensureTwitter('twitter:image', ogImage);
    }

    // Aggiorna alternate hreflang
    const dynamicAlternates = document.querySelectorAll(
      'link[data-dynamic-seo="alternate"]',
    );
    dynamicAlternates.forEach((node) => node.remove());

    alternates.forEach((alternate) => {
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', alternate.hrefLang);
      link.setAttribute('href', alternate.href);
      link.setAttribute('data-dynamic-seo', 'alternate');
      document.head.appendChild(link);
    });

    document
      .querySelectorAll('link[data-dynamic-seo="markdown-alternate"]')
      .forEach((node) => node.remove());
    if (markdownAlternateHref) {
      const mdLink = document.createElement('link');
      mdLink.setAttribute('rel', 'alternate');
      mdLink.setAttribute('type', 'text/markdown');
      mdLink.setAttribute('href', markdownAlternateHref);
      mdLink.setAttribute('title', 'Markdown version');
      mdLink.setAttribute('data-dynamic-seo', 'markdown-alternate');
      document.head.appendChild(mdLink);
    }

    // JSON-LD gestito in modo idempotente (SPA: evita residui tra route)
    document
      .querySelectorAll(
        `script[type="application/ld+json"][${SEO_JSONLD_ATTR}]`,
      )
      .forEach((node) => node.remove());

    if (structuredData) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute(SEO_JSONLD_ATTR, '1');
      script.text = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }

    return () => {
      document
        .querySelectorAll(
          `script[type="application/ld+json"][${SEO_JSONLD_ATTR}]`,
        )
        .forEach((node) => node.remove());
      document
        .querySelectorAll('link[data-dynamic-seo="markdown-alternate"]')
        .forEach((node) => node.remove());
      document
        .querySelectorAll('meta[data-dynamic-seo="article-og"]')
        .forEach((node) => node.remove());
      document.querySelector('meta[property="og:type"]')?.remove();
    };
  }, [
    title,
    description,
    canonical,
    ogImage,
    ogLocale,
    ogType,
    articlePublishedTime,
    articleModifiedTime,
    robots,
    alternates,
    structuredData,
    markdownAlternateHref,
    location.pathname,
  ]);

  return null;
};
